import { type GridColumn } from "@common/controls/Grid/types";
import GridView from "@common/controls/GridView/component";
import { type GridViewExportOptions, type GridViewToolbar } from "@common/controls/GridView/types";
import Icon from "@common/controls/Icon/component";
import MultiSelect from "@common/controls/Inputs/Select/MultiSelect/component";
import { type Api, type GenericMap, IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { EMPTY_ARRAY } from "@common/lib/consts";
import { usePromise } from "@common/lib/hooks";
import { evalInContext, isVisible, uuid } from "@common/lib/misc";
import { isFunction } from "lodash";
import React from "react";
import { type ExtraGridConfigColumn, type GridConfig } from "../types";
import { format } from "./lib";

export interface GridReportProps {
  breadcrumbs?: any;
  drillDownParameters?: GenericMap<any>;
  extraColumns?: ExtraGridConfigColumn<any>[];
  fieldStyle?: React.CSSProperties;
  name: string;
  idKey: string;
  config: GridConfig;
  getData: (args: Api.LoadPageArgs<any>) => Promise<any[]>;
  parameters: GenericMap<any>;

  onRowClick?: (data: any) => void;
}

export default (props: GridReportProps) => {
  const i18n = useI18n();

  const [extraColumns, setExtraColumns] = React.useState<string[]>(
    (props.extraColumns ?? []).filter((x) => x.isDefault).map((x) => x.key)
  );

  React.useEffect(
    () => setExtraColumns((props.extraColumns ?? []).filter((x) => x.isDefault).map((x) => x.key)),
    [props.extraColumns]
  );

  const columns = React.useMemo(
    (): GridColumn<any>[] =>
      [...props.config.columns, ...(props.extraColumns ?? []).filter((x) => extraColumns.includes(x.key))]
        .filter((x) => isVisible(x.visibilityCondition, props.parameters))
        .map((x) => ({
          dataType: x.dataType,
          key: x.key,
          name: x.label,
          data:
            x.format || x.data
              ? (y) => {
                  let result = y[x.key];
                  if (x.data) {
                    if (isFunction(x.data)) {
                      result = x.data(y);
                    } else {
                      result = evalInContext({ f: y }, x.data.replace(/f\./g, "this.f."));
                    }
                  }
                  if (x.format) {
                    result = format(y, x.format);
                  }
                  return result;
                }
              : undefined
        })),
    [props.config.columns, props.parameters, props.extraColumns, extraColumns]
  );

  // TODO: move managed grid view to common and use it
  const [pagination, setPagination] = React.useState<{ pageNumber: number; pageSize: number; search: string }>({
    pageNumber: 1,
    pageSize: props.config.pageSize,
    search: ""
  });

  React.useEffect(() => {
    setPagination((x) => ({
      pageNumber: 1,
      pageSize: x.pageSize,
      search: ""
    }));
  }, [props.getData]);

  React.useEffect(() => {
    setPagination((x) => ({
      pageNumber: x.pageNumber,
      pageSize: props.config.pageSize,
      search: x.search
    }));
  }, [props.config.pageSize]);

  const onPageChanged = React.useCallback((pageNumber: number, pageSize: number) => {
    setPagination((x) => ({ ...x, pageNumber, pageSize }));
  }, []);

  const onSearchChanged = React.useCallback((search: string) => {
    setPagination((x) => ({ ...x, pageNumber: 1, search }));
  }, []);

  const reload = React.useMemo(
    () => uuid(),
    [props.getData, pagination.pageNumber, pagination.pageSize, pagination.search]
  );

  const [data, isLoadingData] = usePromise({
    fn: props.getData,
    args: [{ page: pagination.pageNumber, size: pagination.pageSize, search: pagination.search }],
    shouldLoad: true,
    reload
  });

  const exportOptions = React.useMemo(
    (): GridViewExportOptions<any> => ({
      fileName: props.name,
      getData: () =>
        props.getData({
          page: 1,
          size: 1000000
        }),
      title: i18n.exportReport
    }),
    [i18n, props.getData, props.name]
  );

  const toolbar = React.useMemo(
    (): GridViewToolbar<any> => ({
      showAdd: false,
      showSearch: true,
      leftButtons: [props.breadcrumbs],
      rightButtons: props.extraColumns?.length
        ? [
            <MultiSelect
              // move to right
              style={{ order: 1 }}
              toggle={<Icon icon={IconType.Table} />}
              onChange={(v) => {
                const value = v as string[];
                setExtraColumns(value);
              }}
              options={props.extraColumns.map((x) => ({ label: x.label, value: x.key }))}
              value={extraColumns}
              outputType="array"
            />
          ]
        : undefined
    }),
    [props.breadcrumbs, extraColumns]
  );

  return (
    <div className="grid">
      <GridView
        idKey={props.idKey}
        isLoading={isLoadingData}
        fieldStyle={props.fieldStyle}
        data={data ?? EMPTY_ARRAY}
        exportOptions={exportOptions}
        onPageChanged={onPageChanged}
        columns={columns}
        filterFn={onSearchChanged}
        toolbar={toolbar}
        pageNumber={pagination.pageNumber}
        pageSize={pagination.pageSize}
        onRowClick={props.onRowClick}
      />
    </div>
  );
};
